/* eslint-disable radix */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

import numeral from 'numeral';

import './Input.scss';
import IconError from 'assets/icons/icon-error.svg';


const Input = ({
  label, type, name, value, placeholder, disabled, required, isValid, onInputChange, customClass,
  isSmall, readOnly, onFocus, errorMessage, isTranslate, intl, isSubmitClicked, isCurrency,
  maxLength,
  onBlur = () => {},
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isInputTouched, setInputTouched] = useState(isSubmitClicked);
  const inputClasses = classNames({
    'form-fieldset__input': true,
    'form-fieldset__input--number': type === 'number',
    'form-fieldset__input--disabled': disabled,
    'form-fieldset__input--error': !isValid && (isInputTouched || isSubmitClicked),
    'form-fieldset__input--small': isSmall,
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  /** On Input change event */
  const setValue = (e) => {
    let newVal = e.target.value;
    if (onInputChange) {
      if (!isCurrency) {
        setInputValue(newVal);
        onInputChange(e);
      } else {
        newVal = newVal.replace('-', ''); // do not allow negative value
        const ParseVal = parseInt(numeral(newVal).value());
        const number = +ParseVal;
        setInputValue(number);
        onInputChange(e);
      }
    }
  };

  const onInputBlur = () => {
    setInputTouched(true);
    onBlur();
  };

  /** Handle value we display */
  const getValue = () => {
    if (isCurrency) {
      const currencyValue = numeral(inputValue).format('0,0');
      if (currencyValue === '0') {
        return '';
      }
      return currencyValue;
    }
    return inputValue;
  };

  return (
    <section className="form-fieldset">
      {label && (<span className="form-fieldset__label">{isTranslate ? intl.formatMessage({ id: label }) : label}</span>)}
      <div className={inputClasses}>
        <input
          className={customClass}
          type={type}
          name={name}
          value={getValue()} // inputValue
          placeholder={isTranslate ? intl.formatMessage({ id: placeholder }) : placeholder}
          disabled={disabled}
          required={required}
          onChange={setValue}
          readOnly={readOnly}
          onFocus={onFocus}
          onBlur={onInputBlur}
          maxLength={maxLength}
        />
        {!isValid && (isInputTouched || isSubmitClicked) && <img className="form-fieldset__input__icon" src={IconError} alt="error" />}
      </div>
      {!isValid && (isInputTouched || isSubmitClicked) && errorMessage && (
        <span className="notification notification--error">
          {errorMessage[0]}
        </span>
      )
      }
    </section>
  );
};

Input.propTypes = {
  /** Input Label */
  label: PropTypes.string,
  /** input type */
  type: PropTypes.oneOf(['text', 'number', 'email', 'password', 'tel', 'url']),
  /** input name */
  name: PropTypes.string,
  /** input value */
  value: PropTypes.string,
  /** input placeholder */
  placeholder: PropTypes.string,
  /** input is disabled */
  disabled: PropTypes.bool,
  /** input is required */
  required: PropTypes.bool,
  /** Is there an error */
  isValid: PropTypes.bool,
  /** on input change */
  onInputChange: PropTypes.func,
  /** is input small */
  isSmall: PropTypes.bool,
  /** Addition class name */
  customClass: PropTypes.string,
  /** Is read only */
  readOnly: PropTypes.bool,
  /** Error message */
  errorMessage: PropTypes.arrayOf(PropTypes.string),
  /** translate texts */
  isTranslate: PropTypes.bool,
  /** is Input currency */
  isCurrency: PropTypes.bool,
  /** max number of characters */
  maxLength: PropTypes.number,
};

Input.defaultProps = {
  label: null,
  type: 'text',
  name: '',
  value: '',
  placeholder: '',
  disabled: false,
  required: false,
  isValid: true,
  isSmall: false,
  onInputChange: () => {},
  customClass: '',
  readOnly: false,
  errorMessage: null,
  isTranslate: false,
  isCurrency: false,
  maxLength: null,
  min: 1,
  max: 200,
};

export default injectIntl(Input);
